/**
 * Part 12 — OMS Purchase Audit document (finance-facing support pack).
 * Part 13 — only accounting-appropriate fields (no tokens, emails, cross-venue).
 */

import {
  filterFinanceDocumentsForXeroAudit,
  sanitizeOmsDeepLinkUrl,
  sanitizePersonDisplayName,
  sanitizeTextForXeroAccounting,
} from './xeroExportSanitise'

function isCreditNoteDocument(document = {}) {
  const type = String(document?.documentType || 'invoice')
    .trim()
    .toLowerCase()
  return type === 'creditnote' || type === 'credit_note'
}

function round2(n) {
  const x = Number(n)
  if (!Number.isFinite(x)) return null
  return Math.round((x + Number.EPSILON) * 100) / 100
}

function formatPounds(value) {
  const n = round2(value)
  if (n == null) return null
  return `£${n.toFixed(2)}`
}

function penniesToPounds(pennies) {
  const n = Number(pennies)
  if (!Number.isFinite(n)) return null
  return Math.abs(n) / 100
}

function documentGrossPounds(document = {}) {
  if (document?.invoiceTotal != null) {
    return penniesToPounds(document.invoiceTotal)
  }
  if (document?.gross != null) return round2(document.gross)
  return null
}

function flattenReceiptingRows(receiptingProducts = []) {
  return (Array.isArray(receiptingProducts) ? receiptingProducts.flat() : []).filter(
    Boolean,
  )
}

function sumOrderedReceived(receiptingProducts = []) {
  const rows = flattenReceiptingRows(receiptingProducts)
  let ordered = 0
  let received = 0
  let hasOrdered = false
  let hasReceived = false

  for (const row of rows) {
    const r = row?.receipting || {}
    const orderedQty = Number(
      r.orderedQuantity ?? row?.orderedQuantity ?? row?.quantity ?? 0,
    )
    const orderedPrice = Number(
      r.orderedPrice ?? row?.orderedPrice ?? row?.product?.price ?? row?.price ?? 0,
    )
    if (Number.isFinite(orderedQty) && Number.isFinite(orderedPrice)) {
      ordered += Math.abs(orderedQty) * Math.abs(orderedPrice)
      hasOrdered = true
    }

    if (row?.supplierConfirmed) {
      const recvQty = Number(r.updatedQuantity ?? row?.quantity ?? 0)
      const recvPrice = Number(
        r.updatedPrice ??
          row?.product?.price ??
          row?.price ??
          orderedPrice ??
          0,
      )
      if (Number.isFinite(recvQty) && Number.isFinite(recvPrice)) {
        received += Math.abs(recvQty) * Math.abs(recvPrice)
        hasReceived = true
      }
    }
  }

  return {
    ordered: hasOrdered ? round2(ordered) : null,
    received: hasReceived ? round2(received) : null,
  }
}

function collectPeople({
  order = {},
  document = {},
  receiptingProducts = [],
} = {}) {
  const receiptedBy = new Set()
  for (const row of flattenReceiptingRows(receiptingProducts)) {
    const r = row?.receipting || {}
    const by = sanitizePersonDisplayName(
      r.originalReceiptedBy || r.editedBy || '',
    )
    if (by) receiptedBy.add(by)
  }

  const reconciledBy = [
    ...new Set(
      (Array.isArray(document?.poAllocations) ? document.poAllocations : [])
        .map(a => sanitizePersonDisplayName(a?.reconciledByName))
        .filter(Boolean),
    ),
  ]

  const approvalStatus = String(order?.approvalStatus || '').trim()
  const approvedName = sanitizePersonDisplayName(order?.approvalDecisionBy)
  const approvedBy =
    approvedName ||
    (approvalStatus && approvalStatus !== 'none' ? approvalStatus : null)

  return {
    orderedBy: sanitizePersonDisplayName(order?.placedBy),
    approvedBy,
    receiptedBy: [...receiptedBy],
    reconciledBy,
  }
}

/**
 * Structured OMS Purchase Audit model (Gosia-style finance pack).
 */
export function buildOmsPurchaseAuditModel({
  venue = {},
  order = {},
  venueSupplier = {},
  document = {},
  financeDocuments = [],
  receiptingProducts = [],
  omsUrl = null,
} = {}) {
  const isCredit = isCreditNoteDocument(document)
  const po = String(order?.purchaseOrderNumber || '').trim() || null
  const supplier =
    venueSupplier?.supplierTitle ||
    venueSupplier?.title ||
    order?.supplier?.title ||
    order?.supplierTitle ||
    null

  const venueId = venue?._id || venue?.id || null
  const supplierId =
    venueSupplier?.supplier ||
    venueSupplier?.supplierId ||
    order?.supplier?._id ||
    order?.supplier ||
    null

  const docs = filterFinanceDocumentsForXeroAudit(
    Array.isArray(financeDocuments) ? financeDocuments : [],
    {
      venueId,
      purchaseOrderNumber: po,
      supplierId,
    },
  )
  const invoices = docs.filter(d => d && !isCreditNoteDocument(d) && !d.isArchived)
  const creditNotes = docs.filter(
    d => d && isCreditNoteDocument(d) && !d.isArchived,
  )

  const primaryInvoice = isCredit
    ? invoices[0] || null
    : document?.invoiceNumber || document?._id
      ? document
      : invoices[0] || document || null

  const invoiceNumber = String(
    (isCredit ? primaryInvoice?.invoiceNumber : document?.invoiceNumber) ||
      primaryInvoice?.invoiceNumber ||
      '',
  ).trim() || null

  const creditNoteSummaries = (isCredit ? [document, ...creditNotes] : creditNotes)
    .filter(Boolean)
    .map(cn => {
      const number = String(cn?.invoiceNumber || '').trim()
      const gross = documentGrossPounds(cn)
      return {
        number: number || null,
        gross,
        label:
          number && gross != null
            ? `${number} — ${formatPounds(gross)}`
            : number || (gross != null ? formatPounds(gross) : null),
      }
    })
    .filter(
      (cn, index, arr) =>
        cn.label &&
        arr.findIndex(x => x.number === cn.number && x.gross === cn.gross) ===
          index,
    )

  const { ordered, received } = sumOrderedReceived(receiptingProducts)
  const invoiceValue = documentGrossPounds(
    isCredit ? primaryInvoice || document : document || primaryInvoice,
  )
  const creditNoteTotal = round2(
    creditNoteSummaries.reduce((sum, cn) => sum + (cn.gross || 0), 0),
  )
  const creditRequested =
    creditNoteTotal != null && creditNoteTotal > 0 ? creditNoteTotal : null

  let reconciledLiability = null
  if (invoiceValue != null) {
    reconciledLiability = round2(invoiceValue - (creditNoteTotal || 0))
  }

  const people = collectPeople({ order, document, receiptingProducts })
  const safeUrl = sanitizeOmsDeepLinkUrl(omsUrl)

  return {
    title: 'OMS Purchase Audit',
    kind: isCredit ? 'creditNote' : 'invoice',
    venueTitle: String(venue?.title || '').trim() || null,
    po,
    supplier: supplier ? String(supplier).trim() : null,
    invoiceNumber,
    creditNotes: creditNoteSummaries,
    amounts: {
      ordered,
      received,
      invoiceValue,
      creditRequested,
      creditNoteTotal:
        creditNoteTotal != null && creditNoteTotal > 0 ? creditNoteTotal : null,
      reconciledLiability,
    },
    people,
    omsUrl: safeUrl,
    generatedAt: new Date().toISOString(),
  }
}

/**
 * Plain-text body for the attached OMS Purchase Audit document.
 */
export function formatOmsPurchaseAuditText(model = {}) {
  const lines = []
  lines.push(model.title || 'OMS Purchase Audit')
  lines.push('')

  if (model.venueTitle) lines.push(`Venue: ${model.venueTitle}`)
  if (model.po) lines.push(`PO: ${model.po}`)
  if (model.supplier) lines.push(`Supplier: ${model.supplier}`)
  if (model.invoiceNumber) lines.push(`Invoice: ${model.invoiceNumber}`)

  const amounts = model.amounts || {}
  if (amounts.ordered != null) {
    lines.push(`Ordered: ${formatPounds(amounts.ordered)}`)
  }
  if (amounts.received != null) {
    lines.push(`Received: ${formatPounds(amounts.received)}`)
  }
  if (amounts.invoiceValue != null) {
    lines.push(`Invoice value: ${formatPounds(amounts.invoiceValue)}`)
  }
  if (amounts.creditRequested != null) {
    lines.push(`Credit requested: ${formatPounds(amounts.creditRequested)}`)
  }
  for (const cn of model.creditNotes || []) {
    if (cn?.label) lines.push(`Credit Note: ${cn.label}`)
  }
  if (amounts.reconciledLiability != null) {
    lines.push(
      `Reconciled liability: ${formatPounds(amounts.reconciledLiability)}`,
    )
  }

  lines.push('')
  const people = model.people || {}
  if (people.orderedBy) lines.push(`Ordered by: ${people.orderedBy}`)
  if (people.approvedBy) lines.push(`Approved by: ${people.approvedBy}`)
  if (people.receiptedBy?.length) {
    lines.push(`Receipted by: ${people.receiptedBy.join(', ')}`)
  }
  if (people.reconciledBy?.length) {
    lines.push(`Reconciled by: ${people.reconciledBy.join(', ')}`)
  }

  if (model.omsUrl) {
    lines.push('')
    lines.push(`View in OMS: ${model.omsUrl}`)
  }

  lines.push('')
  lines.push(
    `Generated by OMS ${model.generatedAt ? model.generatedAt.slice(0, 19) + 'Z' : ''}`.trim(),
  )

  return lines.join('\n')
}

export function buildOmsPurchaseAuditDocument(context = {}) {
  const model = buildOmsPurchaseAuditModel(context)
  const text = sanitizeTextForXeroAccounting(formatOmsPurchaseAuditText(model))
  const poSafe = String(model.po || 'PO')
    .replace(/[^a-z0-9._-]+/gi, '_')
    .slice(0, 40)
  return {
    model,
    text,
    fileName: `OMS_Purchase_Audit_${poSafe || 'PO'}.pdf`,
  }
}

/**
 * Short History pointer — detail lives on the attached audit PDF.
 */
export function buildOmsXeroAuditHistoryPointer({
  isCreditNote = false,
  purchaseOrderNumber,
  omsUrl,
  auditFileName = 'OMS_Purchase_Audit.pdf',
} = {}) {
  const kind = isCreditNote ? 'credit note' : 'bill'
  const safeName = String(auditFileName || 'OMS_Purchase_Audit.pdf')
    .replace(/[^a-z0-9._-]+/gi, '_')
    .slice(0, 80)
  const parts = [
    `OMS Purchase Audit attached (${safeName}) for this ${kind}.`,
  ]
  const po = String(purchaseOrderNumber || '').trim()
  if (po && !/^[a-f0-9]{24}$/i.test(po)) parts.push(`PO: ${po}.`)
  const safeUrl = sanitizeOmsDeepLinkUrl(omsUrl)
  if (safeUrl) parts.push(`Open in OMS: ${safeUrl}`)
  return sanitizeTextForXeroAccounting(parts.join(' ')).slice(0, 2000)
}
